import { useEffect, useState } from "react";
import { useAuthActions } from "@convex-dev/auth/react";
import { startAuthentication, type PublicKeyCredentialRequestOptionsJSON } from "@simplewebauthn/browser";
import { useAction, useMutation } from "convex/react";
import { Fingerprint, KeyRound, LockKeyhole, ShieldCheck, Smartphone } from "lucide-react";
import { api } from "../../../../convex/_generated/api";
import { Button } from "../ui/button";
import { errorMessage } from "../workspace/format";
import { EmailCodeVerification } from "./EmailCodeVerification";
import { authenticationOptionsForAuthenticator, detectPasskeyPlatform, passkeyVerificationLabel, type PasskeyAuthenticator } from "./passkeyPlatform";

const PENDING_FLOW_KEY = "yodl-passkey-flow";

export function PasskeyVerification() {
  const passkeyPlatform = detectPasskeyPlatform(navigator.userAgent);
  const windowsPasskey = passkeyPlatform === "windows";
  const { signOut } = useAuthActions();
  const begin = useAction(api.passkeys.beginAuthentication);
  const finish = useAction(api.passkeys.finishSessionAuthentication);
  const claim = useMutation(api.passkeyStore.claimAuthenticationGrant);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeAuthenticator, setActiveAuthenticator] = useState<PasskeyAuthenticator>("hybrid");

  useEffect(() => {
    const flowId = window.sessionStorage.getItem(PENDING_FLOW_KEY);
    if (flowId === null) return;
    setBusy(true);
    void claim({ flowId })
      .then(() => { window.sessionStorage.removeItem(PENDING_FLOW_KEY); })
      .catch(() => { window.sessionStorage.removeItem(PENDING_FLOW_KEY); })
      .finally(() => { setBusy(false); });
  }, [claim]);

  const verify = async (authenticator: PasskeyAuthenticator = windowsPasskey ? "hybrid" : "any") => {
    setBusy(true);
    setError(null);
    setActiveAuthenticator(authenticator);
    try {
      if (!("PublicKeyCredential" in window)) throw new Error("This browser does not support passkeys.");
      const request = await begin({ authenticator });
      const options = authenticationOptionsForAuthenticator(JSON.parse(request.optionsJson) as PublicKeyCredentialRequestOptionsJSON, authenticator);
      const response = await startAuthentication({ optionsJSON: options });
      await finish({ flowId: request.flowId, responseJson: JSON.stringify(response) });
    } catch (caught) {
      const message = errorMessage(caught);
      const domainMismatch = /securityerror|relying party|rp id|domain/iu.test(message);
      setError(message.includes("No Windows Hello passkey") || message.includes("WINDOWS_HELLO_NOT_ENROLLED")
        ? "This account does not have a Windows Hello passkey yet. Use the email code once, then add Windows Hello from Account Security."
        : message.includes("No phone passkey") || message.includes("PHONE_PASSKEY_NOT_ENROLLED")
          ? "This account does not have a phone passkey yet. Use the email code or Windows Hello option."
        : message.includes("No passkey is available") || message.includes("PASSKEY_NOT_ENROLLED")
          ? "No passkey is saved for this account yet. Use the email code below to enter Yodl."
        : message.includes("NotAllowedError")
        ? windowsPasskey
          ? authenticator === "hybrid"
            ? "The phone passkey did not connect. Cancel the Windows box, turn on Bluetooth on both devices, and try the QR button again."
            : authenticator === "local"
              ? "Windows Hello was canceled. Try again and choose Sign-in options, then PIN."
              : "The passkey manager was canceled or did not return a passkey. Try another option."
          : "Passkey verification was canceled or timed out. Try again to enter Yodl."
        : domainMismatch
          ? "Yodl could not confirm this website. Reload https://phiclaw.ai/yodl/ and try again."
          : "We couldn’t verify your passkey. Use Safari or Chrome, then try again.");
    } finally {
      setBusy(false);
    }
  };

  const leave = () => {
    window.sessionStorage.removeItem(PENDING_FLOW_KEY);
    void signOut();
  };

  if (windowsPasskey) {
    return (
      <main className="auth-screen" id="main-content">
        <section className="auth-card passkey-enrollment auth-card--methods" aria-labelledby="passkey-verification-title">
          <div className="brand-mark" aria-label="Yodl"><span>Y</span></div>
          <div className="passkey-enrollment__icon"><ShieldCheck size={30} /></div>
          <h1 id="passkey-verification-title">Choose how to verify</h1>
          <p className="auth-lede">Your password was accepted. Use any one of these four options to enter Yodl.</p>
          <div className="auth-methods" aria-label="Four Yodl verification options">
            <Button className="auth-method" variant="primary" disabled={busy} onClick={() => { void verify("local"); }}>
              <KeyRound size={18} /><span><strong>{busy && activeAuthenticator === "local" ? "Opening Windows Hello…" : "Windows Hello PIN or fingerprint"}</strong><small>Uses a passkey already saved on this PC</small></span>
            </Button>
            <Button className="auth-method" variant="secondary" disabled={busy} onClick={() => { void verify("hybrid"); }}>
              <Smartphone size={18} /><span><strong>{busy && activeAuthenticator === "hybrid" ? "Opening phone QR…" : "Phone QR code"}</strong><small>Use a passkey from iPhone, iPad, or Android</small></span>
            </Button>
            <EmailCodeVerification disabled={busy} />
            <Button className="auth-method" variant="secondary" disabled={busy} onClick={() => { void verify("any"); }}>
              <Fingerprint size={18} /><span><strong>{busy && activeAuthenticator === "any" ? "Opening passkey manager…" : "Apple Passwords or another passkey"}</strong><small>Lets the browser show every available passkey manager</small></span>
            </Button>
          </div>
          {error !== null ? <p className="form-error" role="alert">{error}</p> : null}
          <p className="passkey-enrollment__note">Windows controls the PIN length and available passkey managers. Yodl never receives your PIN, fingerprint, or Apple Passwords data.</p>
          <button type="button" className="link-button" disabled={busy} onClick={leave}>Sign out</button>
        </section>
      </main>
    );
  }

  return (
    <main className="auth-screen" id="main-content">
      <section className="auth-card passkey-enrollment" aria-labelledby="passkey-verification-title">
        <div className="brand-mark" aria-label="Yodl"><span>Y</span></div>
        <div className="passkey-enrollment__icon"><ShieldCheck size={30} /></div>
        <h1 id="passkey-verification-title">Verify it’s you</h1>
        <p className="auth-lede">Use your saved passkey before entering the clinic workspace.</p>
        <div className="passkey-enrollment__benefits">
          <p><Fingerprint size={16} /><span><strong>Required every new sign-in</strong><small>Use Face ID, Touch ID, your fingerprint, or your device PIN.</small></span></p>
          <p><LockKeyhole size={16} /><span><strong>Password alone is not enough</strong><small>This protects the workspace even if someone learns your password.</small></span></p>
        </div>
        {error !== null ? <p className="form-error" role="alert">{error}</p> : null}
        <Button type="button" variant="primary" disabled={busy} onClick={() => { void verify("any"); }}>
          <Fingerprint size={17} /> {busy ? "Waiting for verification…" : passkeyVerificationLabel(passkeyPlatform)}
        </Button>
        <div className="auth-methods" aria-label="Passkey recovery option">
          <EmailCodeVerification disabled={busy} />
        </div>
        <p className="passkey-enrollment__note">If this device cannot find the saved passkey, use the email code above. Password alone still does not open Yodl.</p>
        <button type="button" className="link-button" disabled={busy} onClick={leave}>Sign out</button>
      </section>
    </main>
  );
}
